import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft,
  Send,
  MessageSquare,
  PanelRightClose,
  PanelRightOpen,
  CircleDot,
  CheckCircle2,
  Receipt,
  FileText,
  Check,
  CheckCheck,
  SmilePlus,
  Search,
  ArrowDown,
  Sparkles,
  Shield,
  Paperclip,
  ImagePlus,
  X as XIcon,
  Download,
  Loader2,
} from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useSentryProject } from "@/hooks/use-sentry-project";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { CallDock } from "@/components/calls/CallDock";
import {
  CallEventMessage,
  isCallEventContent,
  parseCallEvent,
} from "@/components/calls/CallEventMessage";


export const Route = createFileRoute("/_authenticated/projects/$id/chat")({
  head: () => ({
    meta: [
      { title: "Project chat — Lumvio" },
      {
        name: "description",
        content:
          "A calm, secure workspace to chat with your client or freelancer while every milestone, invoice, and file stays one glance away.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ProjectChat,
});

const REACTION_EMOJIS = ["👍", "❤️", "🔥", "😂", "😮", "🎉"] as const;

interface Message {
  id: string;
  project_id: string;
  sender_id: string;
  sender_name: string | null;
  content: string;
  created_at: string;
  delivered_at: string | null;
  read_at: string | null;
  attachment_path?: string | null;
  attachment_name?: string | null;
  attachment_type?: string | null;
  attachment_size?: number | null;
}

const URL_REGEX = /(\bhttps?:\/\/[^\s<>"']+|\bwww\.[^\s<>"']+)/gi;
function linkifyText(text: string, mine: boolean): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  URL_REGEX.lastIndex = 0;
  while ((match = URL_REGEX.exec(text)) !== null) {
    const start = match.index;
    if (start > lastIndex) parts.push(text.slice(lastIndex, start));
    let raw = match[0];
    let trailing = "";
    while (raw.length && /[.,;:!?)\]}]/.test(raw[raw.length - 1])) {
      trailing = raw[raw.length - 1] + trailing;
      raw = raw.slice(0, -1);
    }
    const href = raw.startsWith("http") ? raw : `https://${raw}`;
    parts.push(
      <a
        key={`${start}-${raw}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={
          "underline underline-offset-2 break-all hover:opacity-80 " +
          (mine ? "text-primary-foreground/90" : "text-primary")
        }
      >
        {raw}
      </a>,
    );
    if (trailing) parts.push(trailing);
    lastIndex = start + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}




interface ReactionRow {
  id: string;
  message_id: string;
  user_id: string;
  emoji: string;
}

interface ProjectInfo {
  id: string;
  title: string;
  freelancer_id: string;
  client_email: string;
  status: string;
  is_intro: boolean;
}

interface ThreadRow {
  id: string;
  title: string;
  freelancer_id: string;
  client_email: string;
  updated_at: string;
  is_intro: boolean;
}


interface StepRow {
  id: string;
  name: string;
  step_order: number;
  completed: boolean;
  status: string;
}

interface InvoiceRow {
  id: string;
  amount: number;
  net_amount: number;
  status: string;
  description: string | null;
  created_at: string;
}

interface FileRow {
  id: string;
  file_name: string;
  file_size: number | null;
  storage_path: string;
  created_at: string;
}

function initialsOf(name: string | null | undefined, fallback = "?") {
  const src = (name || fallback).trim();
  if (!src) return fallback;
  const parts = src.split(/[\s@._-]+/).filter(Boolean);
  const first = parts[0]?.[0] ?? src[0];
  const second = parts[1]?.[0] ?? "";
  return (first + second).toUpperCase();
}

function avatarGradient(seed: string) {
  // stable hue from seed
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  const hue = h % 360;
  return `linear-gradient(135deg, hsl(${hue} 85% 55%), hsl(${(hue + 40) % 360} 85% 45%))`;
}

function PeerAvatar({
  avatarUrl,
  name,
  seed,
  className,
  textClassName,
}: {
  avatarUrl?: string | null;
  name?: string | null;
  seed: string;
  className?: string;
  textClassName?: string;
}) {
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={name || "avatar"}
        className={cn("shrink-0 rounded-lg object-cover shadow-sm", className)}
      />
    );
  }
  return (
    <div
      className={cn(
        "grid shrink-0 place-items-center rounded-lg font-semibold text-white shadow-sm",
        textClassName,
        className,
      )}
      style={{ background: avatarGradient(seed) }}
      title={name || undefined}
    >
      {initialsOf(name || seed)}
    </div>
  );
}

function dayLabel(d: Date, now: Date, t: (k: string) => string, locale: string) {
  const a = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const b = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const diff = Math.round((b - a) / 86400000);
  if (diff === 0) return t("chat.dayToday");
  if (diff === 1) return t("chat.dayYesterday");
  if (diff < 7)
    return d.toLocaleDateString(locale, { weekday: "long" });
  return d.toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: d.getFullYear() === now.getFullYear() ? undefined : "numeric",
  });
}


function ProjectChat() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("ar") ? "ar" : "en";

  const { id } = useParams({ from: "/_authenticated/projects/$id/chat" });
  useSentryProject(id);
  const { user, profile } = useAuth();
  const qc = useQueryClient();
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const seenIds = useRef<Set<string>>(new Set());
  const [input, setInput] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingPreview, setPendingPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const [contextOpen, setContextOpen] = useState(true);
  const [peerTyping, setPeerTyping] = useState(false);
  const [peerOnline, setPeerOnline] = useState(false);
  const [threadQuery, setThreadQuery] = useState("");
  const [atBottom, setAtBottom] = useState(true);
  const [unreadNew, setUnreadNew] = useState(0);
  const typingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastTypingSent = useRef(0);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const { data: threads } = useQuery({
    queryKey: ["chat-threads", user?.id, profile?.email],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("id,title,freelancer_id,client_email,updated_at,is_intro")
        .order("updated_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as ThreadRow[];
    },
  });

  const { data: project } = useQuery({
    queryKey: ["project", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("id,title,freelancer_id,client_email,status,is_intro")
        .eq("id", id)
        .maybeSingle();
      if (error) throw error;
      return data as ProjectInfo | null;
    },
  });


  const { data: messages } = useQuery({
    queryKey: ["project-messages", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_messages")
        .select("*")
        .eq("project_id", id)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as Message[];
    },
  });

  const { data: steps } = useQuery({
    queryKey: ["project-steps", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_steps")
        .select("id,name,step_order,completed,status")
        .eq("project_id", id)
        .order("step_order", { ascending: true });
      if (error) throw error;
      return (data ?? []) as StepRow[];
    },
  });

  const { data: invoices } = useQuery({
    queryKey: ["project-invoices", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices")
        .select("id,amount,net_amount,status,description,created_at")
        .eq("project_id", id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as InvoiceRow[];
    },
  });

  const { data: files } = useQuery({
    queryKey: ["project-files", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_files")
        .select("id,file_name,file_size,storage_path,created_at")
        .eq("project_id", id)
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      return (data ?? []) as FileRow[];
    },
  });

  const { data: reactions } = useQuery({
    queryKey: ["project-reactions", id],
    enabled: !!messages && messages.length > 0,
    queryFn: async () => {
      const ids = (messages ?? []).map((m) => m.id);
      if (ids.length === 0) return [] as ReactionRow[];
      const { data, error } = await supabase
        .from("message_reactions")
        .select("id,message_id,user_id,emoji")
        .in("message_id", ids);
      if (error) throw error;
      return (data ?? []) as ReactionRow[];
    },
  });

  const reactionsByMessage = useMemo(() => {
    const map = new Map<string, ReactionRow[]>();
    for (const r of reactions ?? []) {
      const list = map.get(r.message_id) ?? [];
      list.push(r);
      map.set(r.message_id, list);
    }
    return map;
  }, [reactions]);

  // Fetch profile pics/names for thread peers + message senders
  const { data: peerProfiles } = useQuery({
    queryKey: [
      "chat-peer-profiles",
      (threads ?? []).map((t) => t.id).join(","),
      (messages ?? []).map((m) => m.sender_id).join(","),
    ],
    enabled: !!user,
    queryFn: async () => {
      const ids = new Set<string>();
      const emails = new Set<string>();
      for (const th of threads ?? []) {
        ids.add(th.freelancer_id);
        if (th.client_email) emails.add(th.client_email.toLowerCase());
      }
      for (const m of messages ?? []) ids.add(m.sender_id);
      const idList = Array.from(ids);
      const emailList = Array.from(emails);
      if (!idList.length && !emailList.length) return [];
      const { data, error } = await supabase.rpc("get_chat_peer_profiles", {
        _ids: idList,
        _emails: emailList,
      });
      if (error) throw error;
      const rows = (data ?? []) as { id: string; email: string | null; full_name: string | null; avatar_url: string | null; portfolio_username: string | null; portfolio_unlocked: boolean | null }[];
      // Resolve storage paths to signed URLs
      await Promise.all(
        rows.map(async (r) => {
          if (r.avatar_url && !r.avatar_url.startsWith("http")) {
            const { data: s } = await supabase.storage.from("avatars").createSignedUrl(r.avatar_url, 60 * 60);
            r.avatar_url = s?.signedUrl ?? null;
          }
        }),
      );
      return rows;
    },


  });

  const profileById = useMemo(() => {
    const m = new Map<string, { name: string | null; avatar: string | null; portfolio_username?: string | null; portfolio_unlocked?: boolean | null }>();
    for (const p of peerProfiles ?? []) m.set(p.id, { name: p.full_name, avatar: p.avatar_url, portfolio_username: p.portfolio_username, portfolio_unlocked: p.portfolio_unlocked });
    return m;
  }, [peerProfiles]);
  const profileByEmail = useMemo(() => {
    const m = new Map<string, { name: string | null; avatar: string | null }>();
    for (const p of peerProfiles ?? []) {
      if (p.email) m.set(p.email.toLowerCase(), { name: p.full_name, avatar: p.avatar_url });
    }
    return m;
  }, [peerProfiles]);

  const isFreelancer = !!(user && project && user.id === project.freelancer_id);
  const otherLabel = useMemo(() => {
    if (!project) return "";
    return isFreelancer
      ? project.client_email
      : t("chat.freelancer", { defaultValue: "Freelancer" });
  }, [project, isFreelancer, t]);

  const STAGE_ORDER = ["brief", "in_progress", "in_review", "completed"] as const;
  const derivedSteps = useMemo(() => {
    if (!steps) return [] as StepRow[];
    const projStatus = project?.status ?? null;
    const projIdx = projStatus ? STAGE_ORDER.indexOf(projStatus as any) : -1;
    return steps.map((s) => {
      const sIdx = STAGE_ORDER.indexOf((s.status ?? "") as any);
      const completed =
        projStatus === "completed"
          ? true
          : sIdx >= 0 && projIdx >= 0
            ? sIdx < projIdx
            : !!s.completed;
      return { ...s, completed };
    });
  }, [steps, project?.status]);

  const currentStep = useMemo(() => {
    if (!derivedSteps || derivedSteps.length === 0) return null;
    const next = derivedSteps.find((s) => !s.completed);
    return next ?? derivedSteps[derivedSteps.length - 1];
  }, [derivedSteps]);

  const activeInvoice = useMemo(() => {
    if (!invoices || invoices.length === 0) return null;
    return (
      invoices.find(
        (i) => i.status === "pending" || i.status === "paid_in_escrow",
      ) ?? invoices[0]
    );
  }, [invoices]);

  const completedCount = derivedSteps.filter((s) => s.completed).length;
  const totalSteps = derivedSteps.length;
  const progressPct = totalSteps
    ? Math.round((completedCount / totalSteps) * 100)
    : 0;

  const filteredThreads = useMemo(() => {
    const q = threadQuery.trim().toLowerCase();
    if (!q) return threads ?? [];
    return (threads ?? []).filter(
      (th) =>
        th.title.toLowerCase().includes(q) ||
        th.client_email.toLowerCase().includes(q),
    );
  }, [threads, threadQuery]);

  // Group messages with date dividers + consecutive-sender grouping
  type Group = {
    key: string;
    dayLabel: string;
    blocks: {
      key: string;
      senderId: string;
      senderName: string | null;
      mine: boolean;
      system?: boolean;
      items: Message[];
    }[];
  };
  const grouped = useMemo<Group[]>(() => {
    const groups: Group[] = [];
    if (!messages) return groups;
    let currentDay: string | null = null;
    let currentGroup: Group | null = null;
    let currentBlock: Group["blocks"][number] | null = null;
    for (const m of messages) {
      const d = new Date(m.created_at);
      const dayKey = d.toDateString();
      if (dayKey !== currentDay) {
        currentDay = dayKey;
        currentGroup = {
          key: dayKey,
          dayLabel: dayLabel(d, new Date(), t, locale),
          blocks: [],
        };
        groups.push(currentGroup);
        currentBlock = null;
      }
      const mine = m.sender_id === user?.id;
      // Call summaries are timeline events, never part of a sender's bubble stack.
      if (isCallEventContent(m.content)) {
        currentGroup!.blocks.push({
          key: m.id,
          senderId: m.sender_id,
          senderName: m.sender_name,
          mine,
          system: true,
          items: [m],
        });
        currentBlock = null;
        continue;
      }
      const lastItemTime = currentBlock
        ? new Date(
            currentBlock.items[currentBlock.items.length - 1].created_at,
          ).getTime()
        : 0;
      const gapMs = d.getTime() - lastItemTime;
      if (
        !currentBlock ||
        currentBlock.senderId !== m.sender_id ||
        gapMs > 5 * 60 * 1000
      ) {
        currentBlock = {
          key: m.id,
          senderId: m.sender_id,
          senderName: m.sender_name,
          mine,
          items: [],
        };
        currentGroup!.blocks.push(currentBlock);
      }
      currentBlock.items.push(m);
    }
    return groups;
  }, [messages, user?.id]);

  const send = useMutation({
    mutationFn: async ({ content, file }: { content: string; file: File | null }) => {
      if (!user) throw new Error("Not signed in");
      let attachment: {
        attachment_path: string;
        attachment_name: string;
        attachment_type: string;
        attachment_size: number;
      } | null = null;

      if (file) {
        if (file.size > 15 * 1024 * 1024) {
          throw new Error("File is larger than 15MB");
        }
        setUploading(true);
        const cleanName = file.name.replace(/[^\w.\-]+/g, "_");
        const path = `${id}/chat/${crypto.randomUUID()}-${cleanName}`;
        const { error: upErr } = await supabase.storage
          .from("project-files")
          .upload(path, file, { contentType: file.type || undefined });
        if (upErr) throw upErr;
        attachment = {
          attachment_path: path,
          attachment_name: file.name,
          attachment_type: file.type || "application/octet-stream",
          attachment_size: file.size,
        };
      }

      const { error } = await supabase.from("project_messages").insert({
        project_id: id,
        sender_id: user.id,
        sender_name: profile?.full_name || profile?.email || "User",
        content,
        ...(attachment ?? {}),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setInput("");
      clearPendingFile();
      qc.invalidateQueries({ queryKey: ["project-messages", id] });
      requestAnimationFrame(() => textareaRef.current?.focus());
    },
    onSettled: () => setUploading(false),
    onError: (e: Error) => toast.error(e.message),
  });


  const toggleReaction = useMutation({
    mutationFn: async ({
      messageId,
      emoji,
    }: {
      messageId: string;
      emoji: string;
    }) => {
      const { error } = await supabase.rpc("toggle_message_reaction", {
        _message_id: messageId,
        _emoji: emoji,
      });
      if (error) throw error;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["project-reactions", id] }),
    onError: (e: Error) => toast.error(e.message),
  });

  // Realtime: messages, reactions + presence & typing broadcast
  useEffect(() => {
    if (!user) return;
    const channel = supabase.channel(`project-room-${id}`, {
      config: { presence: { key: user.id }, broadcast: { self: false } },
    });
    channelRef.current = channel;

    let typingClear: ReturnType<typeof setTimeout> | null = null;

    channel
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "project_messages",
          filter: `project_id=eq.${id}`,
        },
        () => {
          qc.invalidateQueries({ queryKey: ["project-messages", id] });
        },
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "message_reactions",
        },
        () => {
          qc.invalidateQueries({ queryKey: ["project-reactions", id] });
        },
      )
      .on("presence", { event: "sync" }, () => {
        const state = channel.presenceState() as Record<string, unknown[]>;
        const others = Object.keys(state).filter((k) => k !== user.id);
        setPeerOnline(others.length > 0);
      })
      .on("broadcast", { event: "typing" }, (payload) => {
        const from = (payload?.payload as { user_id?: string } | undefined)?.user_id;
        if (!from || from === user.id) return;
        setPeerTyping(true);
        if (typingClear) clearTimeout(typingClear);
        typingClear = setTimeout(() => setPeerTyping(false), 3000);
      })
      .subscribe(async (status) => {
        if (status === "SUBSCRIBED") {
          await channel.track({ user_id: user.id, at: Date.now() });
        }
      });

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
      if (typingClear) clearTimeout(typingClear);
      if (typingTimeout.current) clearTimeout(typingTimeout.current);
    };
  }, [id, qc, user]);


  // Mark delivered / read
  useEffect(() => {
    if (!user) return;
    const markDelivered = () => {
      supabase.rpc("mark_messages_delivered", { _project_id: id }).then(() => {
        qc.invalidateQueries({ queryKey: ["project-messages", id] });
      });
    };
    const markRead = () => {
      if (
        typeof document !== "undefined" &&
        document.visibilityState !== "visible"
      )
        return;
      supabase.rpc("mark_messages_read", { _project_id: id }).then(() => {
        qc.invalidateQueries({ queryKey: ["project-messages", id] });
      });
    };
    markDelivered();
    markRead();
    const onVis = () => markRead();
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [id, user, qc, messages?.length]);

  // Populate seenIds & track new arrivals when scrolled up
  useEffect(() => {
    if (!messages) return;
    if (seenIds.current.size === 0) {
      messages.forEach((m) => seenIds.current.add(m.id));
      return;
    }
    let added = 0;
    for (const m of messages) {
      if (!seenIds.current.has(m.id)) {
        seenIds.current.add(m.id);
        if (m.sender_id !== user?.id) added++;
      }
    }
    if (added > 0 && !atBottom) {
      setUnreadNew((n) => n + added);
    }
  }, [messages, atBottom, user?.id]);

  // Auto-scroll only if at bottom
  useEffect(() => {
    if (!scrollRef.current) return;
    if (atBottom) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
      setUnreadNew(0);
    }
  }, [messages, peerTyping, atBottom]);

  useEffect(() => {
    textareaRef.current?.focus();
  }, [id]);

  function onScroll() {
    const el = scrollRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    const near = dist < 80;
    setAtBottom(near);
    if (near) setUnreadNew(0);
  }

  function scrollToBottom() {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
    setUnreadNew(0);
  }

  function emitTyping() {
    if (!user || !channelRef.current) return;
    const now = Date.now();
    if (now - lastTypingSent.current < 1500) return;
    lastTypingSent.current = now;
    channelRef.current.send({
      type: "broadcast",
      event: "typing",
      payload: { user_id: user.id },
    });
  }


  function clearPendingFile() {
    setPendingFile(null);
    if (pendingPreview) URL.revokeObjectURL(pendingPreview);
    setPendingPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (imageInputRef.current) imageInputRef.current.value = "";
  }

  function pickFile(file: File | null | undefined) {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Max 15MB");
      return;
    }
    setPendingFile(file);
    if (file.type.startsWith("image/")) {
      setPendingPreview(URL.createObjectURL(file));
    } else {
      setPendingPreview(null);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = input.trim();
    if ((!trimmed && !pendingFile) || send.isPending) return;
    send.mutate({ content: trimmed, file: pendingFile });
  }


  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey && !e.altKey) {
      e.preventDefault();
      handleSubmit(e);
      return;
    }
    emitTyping();
  }

  function fmtMoney(n: number) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(n);
  }

  function fmtSize(bytes: number | null) {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  const currentPeer = project
    ? isFreelancer
      ? profileByEmail.get((project.client_email || "").toLowerCase())
      : profileById.get(project.freelancer_id)
    : undefined;
  const currentPeerFreelancer = (!isFreelancer && project) ? profileById.get(project.freelancer_id) : undefined;
  const peerNameForAvatar =
    (project &&
      (isFreelancer
        ? currentPeer?.name || project.client_email
        : currentPeer?.name || "Freelancer")) ||
    "?";
  const peerSeedForAvatar =
    (project &&
      (isFreelancer ? project.client_email || project.id : project.freelancer_id)) ||
    "?";

  return (
    <div className="relative mx-auto flex h-[calc(100dvh-7rem)] sm:h-[calc(100vh-8rem)] w-full max-w-[1400px] gap-4">
      {/* Ambient aurora backdrop */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 overflow-hidden rounded-3xl opacity-60"
      >
        <div className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-fuchsia-500/10 blur-3xl" />
      </div>

      {/* LEFT — Threads */}
      <aside className="hidden w-72 shrink-0 flex-col rounded-2xl border border-border/60 bg-card/50 backdrop-blur-xl md:flex">
        <div className="flex items-center gap-2 border-b border-border/60 px-4 py-4">
          <MessageSquare className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-medium tracking-tight">
            {t("chat.threads", { defaultValue: "Conversations" })}
          </h2>
          <span className="ms-auto rounded-full bg-muted/60 px-2 py-0.5 text-[10px] tabular-nums text-muted-foreground">
            {(threads ?? []).length}
          </span>
        </div>
        <div className="border-b border-border/60 px-3 py-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={threadQuery}
              onChange={(e) => setThreadQuery(e.target.value)}
              placeholder={t("chat.search", { defaultValue: "Search…" })}
              className="h-8 border-border/60 bg-background/40 ps-8 text-xs"
            />
          </div>
        </div>
        <div data-lenis-prevent className="flex-1 overflow-y-auto overscroll-contain px-2 py-2">
          {filteredThreads.length === 0 ? (
            <p className="px-3 py-6 text-xs text-muted-foreground">
              {threadQuery
                ? t("chat.noResults", { defaultValue: "No matches." })
                : t("chat.noThreads", { defaultValue: "No conversations yet." })}
            </p>
          ) : (
            filteredThreads.map((th) => {
              const active = th.id === id;
              const meIsFreelancer = !!user && th.freelancer_id === user.id;
              const peer = meIsFreelancer
                ? profileByEmail.get((th.client_email || "").toLowerCase())
                : profileById.get(th.freelancer_id);
              const otherName = meIsFreelancer
                ? peer?.name || th.client_email
                : peer?.name || t("chat.freelancer", { defaultValue: "Freelancer" });
              return (
                <Link
                  key={th.id}
                  to="/projects/$id/chat"
                  params={{ id: th.id }}
                  className={cn(
                    "group relative flex items-center gap-3 rounded-xl px-2.5 py-2 text-sm transition-all",
                    active
                      ? "bg-primary/10 text-foreground ring-1 ring-primary/25"
                      : "text-muted-foreground hover:bg-muted/40 hover:text-foreground",
                  )}
                >
                  <PeerAvatar
                    avatarUrl={peer?.avatar}
                    name={otherName}
                    seed={meIsFreelancer ? (th.client_email || th.id) : th.freelancer_id}
                    className="h-9 w-9"
                    textClassName="text-[11px]"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{th.is_intro ? otherName : th.title}</div>
                    <div className="truncate text-[11px] opacity-70">
                      {th.is_intro ? t("chat.directMessage", { defaultValue: "Direct message" }) : otherName}
                    </div>

                  </div>
                  {active && (
                    <span className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-primary shadow-[0_0_8px_var(--primary)]" />
                  )}
                </Link>
              );
            })
          )}
        </div>
        <div className="border-t border-border/60 px-4 py-3 text-[10px] text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Shield className="h-3 w-3" />
            {t("chat.encrypted", {
              defaultValue: "Encrypted & payment protection-protected",
            })}
          </span>
        </div>
      </aside>

      {/* CENTER — Chat */}
      <section className="relative flex min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-border/60 bg-card/60 shadow-[var(--shadow-card)] backdrop-blur-xl">
        <header className="flex items-center justify-between gap-3 border-b border-border/60 bg-background/30 px-6 py-3.5">
          <div className="flex min-w-0 items-center gap-3">
            <div className="relative shrink-0">
              <PeerAvatar
                avatarUrl={currentPeer?.avatar}
                name={peerNameForAvatar}
                seed={peerSeedForAvatar}
                className="h-10 w-10 rounded-xl"
                textClassName="text-xs rounded-xl"
              />
              <span
                className={cn(
                  "absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full ring-2 ring-card",
                  peerOnline
                    ? "bg-emerald-500 shadow-[0_0_8px_hsl(142_76%_45%)]"
                    : "bg-muted-foreground/40",
                )}
              />
            </div>
            <div className="min-w-0">
              {!project?.is_intro && (
                <Link
                  to="/projects/$id"
                  params={{ id }}
                  className="inline-flex items-center gap-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
                >
                  <ArrowLeft className="h-3 w-3" /> {t("chat.back")}
                </Link>
              )}
              <h1 className="truncate text-base font-semibold tracking-tight">
                {project?.is_intro
                  ? (currentPeer?.name ||
                      (isFreelancer ? project?.client_email : t("chat.freelancer", { defaultValue: "Freelancer" })))
                  : (project?.title || t("chat.title"))}
              </h1>
              <p className="truncate text-[11px] text-muted-foreground">
                {peerTyping ? (
                  <span className="text-primary">
                    {t("chat.typingNow", {
                      defaultValue: "typing…",
                    })}
                  </span>
                ) : peerOnline ? (
                  <span className="text-emerald-500">
                    {t("chat.online", { defaultValue: "Online now" })}
                  </span>
                ) : project?.is_intro ? (
                  <span>{t("chat.directMessage", { defaultValue: "Direct message" })}</span>
                ) : (
                  <span>
                    {t("chat.conversationWith", { name: otherLabel })}
                  </span>
                )}
              </p>
            </div>

          </div>
          <div className="flex items-center gap-2">
            {!isFreelancer && currentPeerFreelancer?.portfolio_username && (
              <>
                <a
                  href={`/portfolio/${currentPeerFreelancer!.portfolio_username}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-background/50 px-3 py-1.5 text-xs font-medium text-foreground/90 transition-all hover:border-primary/50 hover:bg-primary/10 hover:text-primary"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  {t("chat.viewPortfolio", { defaultValue: "View portfolio" })}
                </a>
                <a
                  href={`/portfolio/${currentPeerFreelancer!.portfolio_username}#reviews`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1.5 text-xs font-medium text-amber-300 transition-all hover:border-amber-300 hover:bg-amber-400/20 hover:text-amber-200"
                >
                  <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.9 6.6L22 9.7l-5.3 4.7L18.2 22 12 18.4 5.8 22l1.5-7.6L2 9.7l7.1-1.1L12 2z"/></svg>
                  {t("chat.rate", { defaultValue: "Rate" })}
                </a>
              </>
            )}
          <CallDock projectId={id} />
          {!project?.is_intro && (

            <Button
              variant="ghost"
              size="icon"
              onClick={() => setContextOpen((v) => !v)}
              className="hidden text-muted-foreground hover:text-foreground lg:inline-flex"
              aria-label={t("chat.aria.toggleContext")}
            >
              {contextOpen ? (
                <PanelRightClose className="h-4 w-4" />
              ) : (
                <PanelRightOpen className="h-4 w-4" />
              )}
            </Button>
          )}
          </div>
        </header>

        <div
          ref={scrollRef}
          onScroll={onScroll}
          data-lenis-prevent
          className="relative flex-1 space-y-6 overflow-y-auto overscroll-contain px-6 py-6"
        >
          {!messages || messages.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-center text-sm text-muted-foreground">
              <div className="relative">
                <div className="absolute inset-0 -z-10 animate-pulse rounded-full bg-primary/20 blur-2xl" />
                <div className="grid h-16 w-16 place-items-center rounded-2xl border border-primary/30 bg-primary/10">
                  <Sparkles className="h-7 w-7 text-primary" />
                </div>
              </div>
              <div>
                <p className="font-medium text-foreground">
                  {t("chat.emptyTitle", {
                    defaultValue: "Start the conversation",
                  })}
                </p>
                <p className="mt-1 max-w-xs text-xs">{t("chat.empty")}</p>
              </div>
            </div>
          ) : (
            grouped.map((g) => (
              <div key={g.key} className="space-y-3">
                <div className="sticky top-0 z-10 flex items-center justify-center">
                  <span className="rounded-full border border-border/50 bg-background/70 px-3 py-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground backdrop-blur-md">
                    {g.dayLabel}
                  </span>
                </div>
                {g.blocks.map((b) => (
                  b.system ? (
                    (() => {
                      const ev = parseCallEvent(b.items[0].content);
                      if (!ev) return null;
                      return (
                        <CallEventMessage
                          key={b.key}
                          event={ev}
                          createdAt={b.items[0].created_at}
                          currentUserId={user?.id}
                        />
                      );
                    })()
                  ) : (
                  <div
                    key={b.key}
                    className={cn(
                      "flex items-end gap-2",
                      b.mine ? "justify-end" : "justify-start",
                    )}
                  >
                    {!b.mine && (() => {
                      const sp = profileById.get(b.senderId);
                      return (
                        <PeerAvatar
                          avatarUrl={sp?.avatar}
                          name={sp?.name || b.senderName}
                          seed={b.senderId}
                          className="mb-1 h-7 w-7"
                          textClassName="text-[10px]"
                        />
                      );
                    })()}
                    <div
                      className={cn(
                        "flex max-w-[72%] flex-col gap-1",
                        b.mine ? "items-end" : "items-start",
                      )}
                    >
                      {!b.mine && b.senderName && (
                        <div className="px-1 text-[11px] font-medium text-muted-foreground">
                          {b.senderName}
                        </div>
                      )}
                      {b.items.map((m, idx) => {
                        const first = idx === 0;
                        const last = idx === b.items.length - 1;
                        const rlist = reactionsByMessage.get(m.id) ?? [];
                        const counts = new Map<
                          string,
                          { count: number; mine: boolean }
                        >();
                        for (const r of rlist) {
                          const prev = counts.get(r.emoji) ?? {
                            count: 0,
                            mine: false,
                          };
                          counts.set(r.emoji, {
                            count: prev.count + 1,
                            mine: prev.mine || r.user_id === user?.id,
                          });
                        }
                        return (
                          <div
                            key={m.id}
                            className={cn(
                              "group/msg relative flex items-center gap-1.5",
                              b.mine ? "flex-row-reverse" : "flex-row",
                            )}
                          >
                            <div
                              className={cn(
                                "relative px-4 py-2 text-sm leading-relaxed shadow-sm backdrop-blur-md border transition-colors",
                                b.mine
                                  ? "border-primary/30 bg-gradient-to-br from-primary/25 to-primary/10 text-foreground"
                                  : "border-border/60 bg-muted/40 text-foreground",
                                // Bubble corner shaping for grouped stack
                                b.mine
                                  ? cn(
                                      "rounded-2xl",
                                      first && "rounded-tr-2xl",
                                      !first && "rounded-tr-md",
                                      last && "rounded-br-md",
                                    )
                                  : cn(
                                      "rounded-2xl",
                                      first && "rounded-tl-2xl",
                                      !first && "rounded-tl-md",
                                      last && "rounded-bl-md",
                                    ),
                                b.mine &&
                                  "shadow-[0_0_24px_-10px_color-mix(in_oklch,var(--primary)_70%,transparent)]",
                              )}
                            >
                              {m.attachment_path && (
                                <MessageAttachment
                                  path={m.attachment_path}
                                  name={m.attachment_name}
                                  type={m.attachment_type}
                                  size={m.attachment_size}
                                />
                              )}
                              {m.content && (
                                <div className="whitespace-pre-wrap break-words">
                                  {linkifyText(m.content, b.mine)}
                                </div>
                              )}


                              <div className="mt-1 flex items-center gap-1 text-[10px] text-muted-foreground/80">
                                <span className="tabular-nums">
                                  {new Date(m.created_at).toLocaleTimeString(
                                    [],
                                    { hour: "2-digit", minute: "2-digit" },
                                  )}
                                </span>
                                {b.mine && (
                                  <span className="ms-1 inline-flex items-center">
                                    {m.read_at ? (
                                      <CheckCheck className="h-3.5 w-3.5 text-primary drop-shadow-[0_0_4px_color-mix(in_oklch,var(--primary)_70%,transparent)]" />
                                    ) : m.delivered_at ? (
                                      <CheckCheck className="h-3.5 w-3.5 text-muted-foreground/70" />
                                    ) : (
                                      <Check className="h-3.5 w-3.5 text-muted-foreground/70" />
                                    )}
                                  </span>
                                )}
                              </div>
                            </div>
                            <Popover>
                              <PopoverTrigger asChild>
                                <button
                                  className="opacity-0 transition-opacity group-hover/msg:opacity-100 text-muted-foreground hover:text-foreground"
                                  aria-label={t("chat.aria.addReaction")}
                                >
                                  <SmilePlus className="h-4 w-4" />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent
                                className="w-auto p-1 border-border/60 bg-popover/95 backdrop-blur-md"
                                side="top"
                              >
                                <div className="flex gap-1">
                                  {REACTION_EMOJIS.map((e) => (
                                    <button
                                      key={e}
                                      onClick={() =>
                                        toggleReaction.mutate({
                                          messageId: m.id,
                                          emoji: e,
                                        })
                                      }
                                      className="rounded-md px-2 py-1 text-lg transition-transform hover:scale-125 hover:bg-muted/60"
                                    >
                                      {e}
                                    </button>
                                  ))}
                                </div>
                              </PopoverContent>
                            </Popover>
                            {counts.size > 0 && (
                              <div
                                className={cn(
                                  "absolute -bottom-3 flex flex-wrap gap-1",
                                  b.mine ? "right-2" : "left-2",
                                )}
                              >
                                {Array.from(counts.entries()).map(
                                  ([emoji, { count, mine: isMine }]) => (
                                    <button
                                      key={emoji}
                                      onClick={() =>
                                        toggleReaction.mutate({
                                          messageId: m.id,
                                          emoji,
                                        })
                                      }
                                      className={cn(
                                        "inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] shadow-sm transition-colors",
                                        isMine
                                          ? "border-primary/40 bg-primary/20 text-foreground"
                                          : "border-border/60 bg-background/80 text-muted-foreground hover:text-foreground",
                                      )}
                                    >
                                      <span>{emoji}</span>
                                      <span className="tabular-nums">
                                        {count}
                                      </span>
                                    </button>
                                  ),
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  )
                ))}
              </div>
            ))
          )}

          {peerTyping && (
            <div className="flex items-end gap-2">
              <PeerAvatar
                avatarUrl={currentPeer?.avatar}
                name={peerNameForAvatar}
                seed={peerSeedForAvatar}
                className="mb-1 h-7 w-7"
                textClassName="text-[10px]"
              />
              <div className="flex items-center gap-1 rounded-2xl rounded-bl-md border border-border/60 bg-muted/50 px-4 py-2.5 backdrop-blur-md">
                <span className="typing-dot" />
                <span className="typing-dot [animation-delay:150ms]" />
                <span className="typing-dot [animation-delay:300ms]" />
              </div>
            </div>
          )}
        </div>

        {/* Floating "new messages" pill */}
        {!atBottom && (
          <button
            onClick={scrollToBottom}
            className="absolute bottom-28 left-1/2 z-20 inline-flex -translate-x-1/2 items-center gap-2 rounded-full border border-primary/30 bg-primary/90 px-3 py-1.5 text-xs font-medium text-primary-foreground shadow-lg shadow-primary/30 backdrop-blur-md transition-all hover:bg-primary"
          >
            <ArrowDown className="h-3.5 w-3.5" />
            {unreadNew > 0
              ? t("chat.newMessages", {
                  count: unreadNew,
                  defaultValue: `${unreadNew} new message${unreadNew > 1 ? "s" : ""}`,
                })
              : t("chat.jumpLatest", { defaultValue: "Jump to latest" })}
          </button>
        )}

        <form
          onSubmit={handleSubmit}
          className="border-t border-border/60 bg-background/30 px-6 py-4"
        >
          {pendingFile && (
            <div className="mb-2 flex items-center gap-3 rounded-xl border border-border/60 bg-muted/30 p-2 pe-3 text-xs">
              {pendingPreview ? (
                <img
                  src={pendingPreview}
                  alt=""
                  className="h-12 w-12 rounded-lg object-cover"
                />
              ) : (
                <div className="grid h-12 w-12 place-items-center rounded-lg bg-primary/10 text-primary">
                  <FileText className="h-5 w-5" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{pendingFile.name}</div>
                <div className="text-muted-foreground">
                  {(pendingFile.size / 1024 / 1024).toFixed(2)} MB
                </div>
              </div>
              <button
                type="button"
                onClick={clearPendingFile}
                className="rounded-full p-1 text-muted-foreground hover:bg-muted"
                aria-label={t("chat.aria.removeAttachment")}
              >
                <XIcon className="h-4 w-4" />
              </button>
            </div>
          )}
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => pickFile(e.target.files?.[0])}
          />
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={(e) => pickFile(e.target.files?.[0])}
          />
          <div className="group flex items-end gap-2 rounded-2xl border border-border/60 bg-background/50 p-2 transition-all focus-within:border-primary/50 focus-within:shadow-[0_0_0_4px_color-mix(in_oklch,var(--primary)_10%,transparent)]">
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={() => imageInputRef.current?.click()}
              className="shrink-0 rounded-xl text-muted-foreground hover:text-primary"
              aria-label={t("chat.aria.attachImage")}
            >
              <ImagePlus className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={() => fileInputRef.current?.click()}
              className="shrink-0 rounded-xl text-muted-foreground hover:text-primary"
              aria-label={t("chat.aria.attachFile")}
            >
              <Paperclip className="h-4 w-4" />
            </Button>
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                emitTyping();
              }}
              onKeyDown={handleKeyDown}
              placeholder={t("chat.placeholder")}
              rows={1}
              className="min-h-[40px] resize-none border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
              maxLength={4000}
            />
            <Button
              type="submit"
              size="icon"
              disabled={(!input.trim() && !pendingFile) || send.isPending || uploading}
              className="shrink-0 rounded-xl transition-transform active:scale-95"
              aria-label={t("chat.aria.send", "Send message")}
            >
              {uploading || send.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>

          <div className="mt-2 flex items-center justify-between px-1 text-[11px] text-muted-foreground">
            <span>
              <kbd className="rounded border border-border/60 bg-muted/40 px-1 py-0.5 text-[10px]">
                Enter
              </kbd>{" "}
              {t("chat.hintSend", { defaultValue: "to send" })} ·{" "}
              <kbd className="rounded border border-border/60 bg-muted/40 px-1 py-0.5 text-[10px]">
                Shift+Enter
              </kbd>{" "}
              {t("chat.hintNewline", { defaultValue: "for newline" })}
            </span>
            <span
              className={cn(
                "tabular-nums",
                input.length > 3600 && "text-amber-500",
                input.length >= 4000 && "text-red-500",
              )}
            >
              {input.length}/4000
            </span>
          </div>
        </form>
      </section>

      {/* RIGHT — Context */}
      {contextOpen && !project?.is_intro && (
        <aside data-lenis-prevent className="hidden w-72 shrink-0 flex-col gap-4 overflow-y-auto overscroll-contain lg:flex animate-fade-in">
          {/* Milestone */}
          <div className="rounded-2xl border border-border/60 bg-card/60 p-5 backdrop-blur-xl">
            <div className="mb-3 flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
              <CircleDot className="h-3.5 w-3.5" />
              {t("chat.context.milestone", {
                defaultValue: "Current milestone",
              })}
            </div>
            {currentStep ? (
              <>
                <p className="text-base font-medium tracking-tight">
                  {currentStep.name}
                </p>
                {totalSteps > 0 && (
                  <div className="mt-3">
                    <div className="mb-1 flex items-center justify-between text-[10px] text-muted-foreground">
                      <span>
                        {completedCount}/{totalSteps}
                      </span>
                      <span className="tabular-nums">{progressPct}%</span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-muted/40">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-primary to-fuchsia-500 shadow-[0_0_8px_var(--primary)] transition-all duration-700"
                        style={{ width: `${progressPct}%` }}
                      />
                    </div>
                  </div>
                )}
                <div className="mt-4 space-y-2">
                  {derivedSteps.map((s) => (
                    <div key={s.id} className="flex items-center gap-2 text-xs">
                      {s.completed ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                      ) : (
                        <CircleDot
                          className={cn(
                            "h-3.5 w-3.5",
                            s.id === currentStep.id
                              ? "text-primary"
                              : "text-muted-foreground/50",
                          )}
                        />
                      )}
                      <span
                        className={cn(
                          s.completed
                            ? "text-muted-foreground line-through"
                            : s.id === currentStep.id
                              ? "text-foreground"
                              : "text-muted-foreground",
                        )}
                      >
                        {s.name}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">
                {t("chat.context.noMilestone", {
                  defaultValue: "No milestones yet.",
                })}
              </p>
            )}
          </div>

          {/* Invoice */}
          <div className="rounded-2xl border border-border/60 bg-card/60 p-5 backdrop-blur-xl">
            <div className="mb-3 flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
              <Receipt className="h-3.5 w-3.5" />
              {t("chat.context.invoice", { defaultValue: "Active invoice" })}
            </div>
            {activeInvoice ? (
              <>
                <p className="text-2xl font-semibold tracking-tight">
                  {fmtMoney(Number(activeInvoice.amount))}
                </p>
                <p className="mt-1 text-xs capitalize text-muted-foreground">
                  {activeInvoice.status.replace(/_/g, " ")}
                </p>
                {activeInvoice.description && (
                  <p className="mt-2 line-clamp-2 text-xs text-muted-foreground/80">
                    {activeInvoice.description}
                  </p>
                )}
                <Link
                  to="/invoices"
                  className="mt-3 inline-block text-xs text-primary hover:underline"
                >
                  {t("chat.context.viewAll", {
                    defaultValue: "View all invoices",
                  })}{" "}
                  →
                </Link>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">
                {t("chat.context.noInvoice", {
                  defaultValue: "No invoices yet.",
                })}
              </p>
            )}
          </div>

          {/* Files */}
          <div className="rounded-2xl border border-border/60 bg-card/60 p-5 backdrop-blur-xl">
            <div className="mb-3 flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
              <FileText className="h-3.5 w-3.5" />
              {t("chat.context.files", { defaultValue: "Shared files" })}
            </div>
            {(files ?? []).length === 0 ? (
              <p className="text-xs text-muted-foreground">
                {t("chat.context.noFiles", {
                  defaultValue: "No files shared yet.",
                })}
              </p>
            ) : (
              <ul className="space-y-2">
                {(files ?? []).slice(0, 6).map((f) => (
                  <li
                    key={f.id}
                    className="flex items-center justify-between gap-2 rounded-lg border border-border/40 bg-background/30 px-3 py-2 text-xs transition-colors hover:border-primary/40 hover:bg-primary/5"
                  >
                    <span className="truncate text-foreground">
                      {f.file_name}
                    </span>
                    <span className="shrink-0 text-muted-foreground">
                      {fmtSize(f.file_size)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      )}
    </div>
  );
}

function MessageAttachment({
  path,
  name,
  type,
  size,
}: {
  path: string;
  name?: string | null;
  type?: string | null;
  size?: number | null;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const isImage = (type || "").startsWith("image/");
  useEffect(() => {
    let cancelled = false;
    supabase.storage
      .from("project-files")
      .createSignedUrl(path, 60 * 60)
      .then(({ data }) => {
        if (!cancelled && data?.signedUrl) setUrl(data.signedUrl);
      });
    return () => {
      cancelled = true;
    };
  }, [path]);

  if (isImage) {
    return (
      <a
        href={url ?? "#"}
        target="_blank"
        rel="noreferrer"
        className="mb-1 block overflow-hidden rounded-lg border border-border/40"
      >
        {url ? (
          <img src={url} alt={name || ""} className="max-h-64 w-full object-cover" />
        ) : (
          <div className="h-32 w-64 animate-pulse bg-muted/40" />
        )}
      </a>
    );
  }
  return (
    <a
      href={url ?? "#"}
      target="_blank"
      rel="noreferrer"
      className="mb-1 flex items-center gap-2 rounded-lg border border-border/40 bg-background/40 p-2 text-xs hover:border-primary/40 hover:bg-primary/5"
    >
      <div className="grid h-9 w-9 place-items-center rounded-md bg-primary/10 text-primary">
        <FileText className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium">{name || "Attachment"}</div>
        {size ? (
          <div className="text-muted-foreground">
            {(size / 1024 / 1024).toFixed(2)} MB
          </div>
        ) : null}
      </div>
      <Download className="h-4 w-4 shrink-0 text-muted-foreground" />
    </a>
  );
}
